/**
 * useStrategyTargets — the Cibles targets a Brand Knowledge list item can be scoped to
 * (`BrandStrategy.itemTargets`), plus the reconciliation of stored ids.
 *
 * Two reads, each with its own failure rule:
 *  1. OPTIONS: the brand's own targets (`ownerId=own`) of the pickable categories, paged
 *     up to `MAX_PAGES` per category. Hitting the bound sets `truncated` (a silently short
 *     picker reads as "this brand has 1 000 products"); a failed category sets `error`.
 *  2. BY-ID: every stored id (`itemTargets` values AND `marqueProfiles` keys) the options
 *     do not contain is resolved through
 *     `POST /targets/by-id`. This is also how a mapping to a category that is no
 *     longer pickable (produit, collection) still DISPLAYS. ONLY its `notFound` feeds `missingIds`, and only
 *     `missingIds` is ever dropped on save. A failed lookup leaves its ids out of
 *     `missingIds` (fail closed: an outage must never wipe a user's mappings).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  TARGET_LIMITS,
  type MarqueProfile,
  type StrategyItemTargets,
  type StrategyScopedList,
  type TargetCategory,
  type TargetColorToken,
} from '@traaker/shared';
import { api } from '../../../services/api';
import { targetsService } from '../../../services/targets';
import { reconcileItemTargets, reconcileMarqueProfiles } from './scopedList';

export { reconcileItemTargets, reconcileMarqueProfiles };

export interface StrategyTargetOption {
  targetId: string;
  label: string;
  category: TargetCategory;
  color?: TargetColorToken;
  parentTargetId?: string;
  /** `marque` options only: the Cibles `alias` / `domain` field values, for display. */
  aliases?: string[];
  domains?: string[];
}

export interface StrategyTargets {
  /** Pickable OWN targets: marque, etablissement, personne. */
  options: StrategyTargetOption[];
  loading: boolean;
  resolve: (targetId: string) => StrategyTargetOption | undefined;
  /** Ids CONFIRMED absent by `POST /brands/:id/targets/by-id` (`notFound`). */
  missingIds: Set<string>;
  /** Drops entries whose id is in missingIds or whose item is not in `lists[field]`; returns undefined when empty. */
  reconcile: (
    it: StrategyItemTargets | undefined,
    lists: Partial<Record<StrategyScopedList, string[]>>,
  ) => StrategyItemTargets | undefined;
  /** Drops empty profiles and profiles whose marque id is in missingIds; returns undefined when empty. */
  reconcileMarqueProfiles: (
    profiles: Record<string, MarqueProfile> | undefined,
  ) => Record<string, MarqueProfile> | undefined;
  /** At least one category had more than `PICKER_MAX_PER_CATEGORY` targets: `options` is partial. */
  truncated: boolean;
  /** At least one category failed to load: `options` is partial. */
  error: boolean;
}

/**
 * Order = display order in the picker. `contenu`, `offre`, `produit` and `collection`
 * are not offered; an existing mapping to a produit / collection still resolves (by-id).
 */
export const PICKABLE_CATEGORIES: TargetCategory[] = ['marque', 'etablissement', 'personne'];

const PAGE_SIZE = 200;
const MAX_PAGES = 5;
export const PICKER_MAX_PER_CATEGORY = PAGE_SIZE * MAX_PAGES;

interface ListRow {
  targetId: string;
  ownerId: string;
  category: TargetCategory;
  label?: string;
  color?: TargetColorToken | null;
  parentTargetId?: string;
  fields?: { name: string; value: unknown }[];
}

const fieldValues = (row: ListRow, name: string): string[] =>
  (row.fields ?? [])
    .filter((f) => f.name === name && typeof f.value === 'string' && f.value.trim())
    .map((f) => f.value as string);

async function loadCategory(
  brandId: string,
  category: TargetCategory,
): Promise<{ rows: ListRow[]; truncated: boolean }> {
  const rows: ListRow[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < MAX_PAGES; page++) {
    const params = new URLSearchParams({ category, ownerId: 'own', limit: String(PAGE_SIZE) });
    if (cursor) params.set('cursor', cursor);
    const data = await api.get<{ items: ListRow[]; nextCursor: string | null }>(`/brands/${brandId}/targets?${params}`);
    rows.push(...(data?.items ?? []));
    cursor = data?.nextCursor ?? null;
    if (!cursor) return { rows, truncated: false };
  }
  return { rows, truncated: true };
}

function collectIds(it: StrategyItemTargets | undefined, extraIds: readonly string[] = []): string[] {
  const ids = new Set<string>(extraIds.filter(Boolean));
  for (const map of Object.values(it ?? {})) for (const id of Object.values(map ?? {})) if (id) ids.add(id);
  return [...ids].sort();
}

export function useStrategyTargets(
  brandId: string | undefined,
  /** The LOADED item scopes (`useKnowledgeScopes`), not the brand row's legacy copy. */
  itemTargets: StrategyItemTargets | undefined,
  /** Other stored ids to resolve the same way (the `marqueProfiles` keys). */
  extraIds?: readonly string[],
): StrategyTargets {
  const [options, setOptions] = useState<StrategyTargetOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsReady, setOptionsReady] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState(false);
  const [extra, setExtra] = useState<Map<string, StrategyTargetOption>>(new Map());
  const [missingIds, setMissingIds] = useState<Set<string>>(new Set());
  const [lookupPending, setLookupPending] = useState(false);
  /** Ids already sent to by-id for this brand (resolved, missing OR failed) — never re-asked. */
  const attempted = useRef<Set<string>>(new Set());

  // 1. Options — once per brand.
  useEffect(() => {
    setOptions([]);
    setExtra(new Map());
    setMissingIds(new Set());
    setTruncated(false);
    setError(false);
    setOptionsReady(false);
    attempted.current = new Set();
    if (!brandId) return;
    let cancelled = false;
    setOptionsLoading(true);
    Promise.allSettled(PICKABLE_CATEGORIES.map((c) => loadCategory(brandId, c))).then((results) => {
      if (cancelled) return;
      const rows: ListRow[] = [];
      let trunc = false;
      let failed = false;
      for (const r of results) {
        if (r.status === 'fulfilled') { rows.push(...r.value.rows); trunc ||= r.value.truncated; }
        else failed = true;
      }
      const own = rows.filter((r) => r.ownerId === 'own');
      const marqueColor = new Map(own.filter((r) => r.category === 'marque').map((r) => [r.targetId, r.color ?? undefined]));
      setOptions(
        own.map((r) => ({
          targetId: r.targetId,
          label: r.label || r.targetId.slice(0, 8),
          category: r.category,
          // A child's swatch is its marque's colour: colour is a marque attribute (D45).
          color: (r.category === 'marque' ? r.color : r.parentTargetId ? marqueColor.get(r.parentTargetId) : undefined) ?? undefined,
          parentTargetId: r.parentTargetId,
          ...(r.category === 'marque' ? { aliases: fieldValues(r, 'alias'), domains: fieldValues(r, 'domain') } : {}),
        })),
      );
      setTruncated(trunc);
      setError(failed);
      setOptionsLoading(false);
      setOptionsReady(true);
    });
    return () => { cancelled = true; };
  }, [brandId]);

  const optionById = useMemo(() => new Map(options.map((o) => [o.targetId, o])), [options]);

  // 2. By-id — the stored ids the options do not cover, each asked once per brand.
  const storedIdsKey = collectIds(itemTargets, extraIds).join(',');
  useEffect(() => {
    if (!brandId || !optionsReady) return;
    const unknown = (storedIdsKey ? storedIdsKey.split(',') : []).filter(
      (id) => !optionById.has(id) && !attempted.current.has(id),
    );
    if (!unknown.length) return;
    for (const id of unknown) attempted.current.add(id);
    let cancelled = false;
    setLookupPending(true);
    const chunk = TARGET_LIMITS.maxTargetsByIdLookup;
    const batches: string[][] = [];
    for (let i = 0; i < unknown.length; i += chunk) batches.push(unknown.slice(i, i + chunk));
    Promise.allSettled(batches.map((ids) => targetsService.getByIds(brandId, ids.map((targetId) => ({ targetId })))))
      .then((results) => {
        if (cancelled) return;
        const found: StrategyTargetOption[] = [];
        const notFound: string[] = [];
        for (const r of results) {
          if (r.status !== 'fulfilled') continue; // fail closed: these ids stay unresolved, never missing
          for (const item of r.value.items) {
            found.push({ targetId: item.targetId, label: item.label || item.url || item.targetId.slice(0, 8), category: item.category });
          }
          notFound.push(...r.value.notFound);
        }
        if (found.length) setExtra((prev) => { const next = new Map(prev); for (const o of found) next.set(o.targetId, o); return next; });
        if (notFound.length) setMissingIds((prev) => new Set([...prev, ...notFound]));
        setLookupPending(false);
      });
    return () => {
      cancelled = true;
      // An interrupted lookup is re-asked by the next run rather than lost.
      for (const id of unknown) attempted.current.delete(id);
      setLookupPending(false);
    };
  }, [brandId, optionsReady, optionById, storedIdsKey]);

  const resolve = useCallback(
    (targetId: string) => (missingIds.has(targetId) ? undefined : optionById.get(targetId) ?? extra.get(targetId)),
    [optionById, extra, missingIds],
  );

  const reconcile = useCallback<StrategyTargets['reconcile']>(
    (it, lists) => reconcileItemTargets(it, lists, missingIds),
    [missingIds],
  );

  const reconcileProfiles = useCallback<StrategyTargets['reconcileMarqueProfiles']>(
    (profiles) => reconcileMarqueProfiles(profiles, missingIds),
    [missingIds],
  );

  return useMemo(
    () => ({
      options, loading: optionsLoading || lookupPending, resolve, missingIds, reconcile,
      reconcileMarqueProfiles: reconcileProfiles, truncated, error,
    }),
    [options, optionsLoading, lookupPending, resolve, missingIds, reconcile, reconcileProfiles, truncated, error],
  );
}
